<!--
	CompareSlider — the before/after wipe. Two renders of the SAME part stacked on
	the app's dark canvas; a native range input (keyboard, focus ring, no-JS-safe
	at 50%) drags the seam between "what the scanner saw" and "what the plotter
	gets". The handle is a registration tick — the product's own mark.

	`clip-path` on the top layer, one custom property, no layout work per frame.
-->
<script lang="ts">
	let {
		beforeSrc,
		afterSrc,
		alt,
		width,
		height,
		beforeLabel,
		afterLabel,
		class: klass = ''
	}: {
		beforeSrc: string;
		afterSrc: string;
		alt: string;
		width: number;
		height: number;
		beforeLabel: string;
		afterLabel: string;
		class?: string;
	} = $props();

	/** Seam position, 0..100 (%). */
	let x = $state(50);
</script>

<figure class="compare {klass}" style:--x="{x}%">
	<div class="compare__stage app-surface" style:aspect-ratio="{width} / {height}">
		<img class="compare__img" src={afterSrc} {alt} {width} {height} loading="lazy" />
		<img
			class="compare__img compare__img--before"
			src={beforeSrc}
			{alt}
			{width}
			{height}
			loading="lazy"
			aria-hidden="true"
		/>
		<div class="compare__seam" aria-hidden="true">
			<span class="compare__grip"></span>
		</div>
		<span class="compare__tag compare__tag--before">{beforeLabel}</span>
		<span class="compare__tag compare__tag--after">{afterLabel}</span>
		<input
			class="compare__range"
			type="range"
			min="0"
			max="100"
			step="0.5"
			bind:value={x}
			aria-label="Compare the 3D scan with the flattened cut template"
		/>
	</div>
</figure>

<style>
	.compare {
		margin: 0;
		border-radius: var(--radius-win);
		box-shadow: var(--shadow-win);
		overflow: hidden;
	}
	.compare__stage {
		position: relative;
		overflow: hidden;
	}
	.compare__img {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
	.compare__img--before {
		clip-path: inset(0 calc(100% - var(--x)) 0 0);
	}

	/* ── The seam ───────────────────────────────────────────────────────── */
	.compare__seam {
		position: absolute;
		top: 0;
		bottom: 0;
		left: var(--x);
		width: 2px;
		margin-left: -1px;
		background-color: var(--color-cut-400);
		pointer-events: none;
	}
	.compare__grip {
		position: absolute;
		top: 50%;
		left: 50%;
		width: 30px;
		height: 30px;
		transform: translate(-50%, -50%);
		border-radius: 50%;
		background-color: var(--color-cut-400);
		box-shadow: 0 2px 10px rgba(12, 13, 11, 0.45);
	}
	/* Registration-mark grip: a crosshair cut into the dot. */
	.compare__grip::before,
	.compare__grip::after {
		content: '';
		position: absolute;
		background-color: var(--color-ink-900);
	}
	.compare__grip::before {
		left: 50%;
		top: 6px;
		bottom: 6px;
		width: 1.5px;
		margin-left: -0.75px;
	}
	.compare__grip::after {
		top: 50%;
		left: 6px;
		right: 6px;
		height: 1.5px;
		margin-top: -0.75px;
	}

	/* ── Corner tags — the readout voice on the dark canvas ─────────────── */
	.compare__tag {
		position: absolute;
		top: 0.75rem;
		padding: 0.25rem 0.5rem;
		border-radius: var(--radius-sm);
		border: 1px solid var(--color-app-700);
		background-color: color-mix(in srgb, var(--color-app-900) 88%, transparent);
		font-family: var(--font-mono);
		font-size: var(--text-mono-xs);
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--color-bone-300);
		pointer-events: none;
	}
	.compare__tag--before {
		left: 0.75rem;
	}
	.compare__tag--after {
		right: 0.75rem;
		color: var(--color-cut-400);
	}

	/* ── The control — invisible, covers the stage, still the focus target ─ */
	.compare__range {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		margin: 0;
		opacity: 0;
		cursor: ew-resize;
		-webkit-appearance: none;
		appearance: none;
	}
	.compare__range:focus-visible {
		opacity: 0;
		outline: none;
	}
	/* The input covers the stage, so :focus-within is the ring — on the seam. */
	.compare__stage:focus-within .compare__seam {
		box-shadow: 0 0 0 2px var(--color-cut-400);
	}
</style>
